import { VOUCHER_APPLIED, voucherReasonMessage } from './tenderWording.js';

/**
 * The platform's answer about the code that was typed, as `POST /pricing/quote` sends it.
 *
 * Three states, and the screen must not collapse them: `undefined` means nothing was asked (or the
 * answer is not in yet), `applied: false` means it was refused, `applied: true` means it priced in.
 * Asking nothing is not a refusal.
 */
export interface VoucherVerdict {
  applied?: boolean;
  reason?: string;
  discount_minor?: number;
}

interface VoucherStepProps {
  /** Controlled voucher code, exactly as the renter typed it. */
  code: string;
  onChange: (code: string) => void;
  /** The quote's verdict on `code`, or `undefined` while none applies. */
  verdict?: VoucherVerdict;
}

/**
 * 065: the voucher tender — its label, its input, and (from T010) the platform's verdict on the
 * code that was typed.
 *
 * Extracted from `BookingFormPage` so this step mirrors `GiftCertStep` beside it. The two tenders
 * sit one control apart and must refuse a code the same way (FR-005, US3); a component boundary
 * makes that structural instead of merely intended, and it lets the ten verdict cases be tested
 * directly rather than through four date-picker clicks each.
 */
export function VoucherStep({ code, onChange, verdict }: VoucherStepProps) {
  return (
    <div className="flex flex-col gap-1">
      {/* A voucher and a gift certificate are different tenders but sit next to each other,
          so each box names its own kind of code — a shared "ENTER CODE" placeholder left the
          renter guessing. `htmlFor` also gives the field a real accessible name. */}
      <label className="text-sm text-gray-400" htmlFor="voucher-code">
        Voucher Code (optional)
      </label>
      <input
        id="voucher-code"
        type="text"
        className="bg-surface-mid text-text rounded-lg px-4 py-3 uppercase"
        value={code}
        onInput={(e) => onChange((e.target as HTMLInputElement).value)}
        placeholder="ENTER VOUCHER CODE"
      />
      {/* The refusal belongs beside the box that caused it, and must leave the rest of the form
          usable — the renter can still fix the code, change dates, or continue without it
          (FR-005). Same position and treatment as `GiftCertStep`'s own refusal. */}
      {verdict && !verdict.applied && (
        <p data-testid="voucher-verdict" className="text-sm text-red-400">
          {voucherReasonMessage(verdict.reason)}
        </p>
      )}
      {/* An accepted code needs saying too. The price summary's "Voucher discount" line is guarded
          on the amount being truthy, so a voucher applied at zero — the seeded `SUMMER` code, which
          unlocks a code-gated rule rather than discounting directly — draws no line and would leave
          this screen indistinguishable from one where nothing was entered (FR-008). */}
      {verdict?.applied && (
        <p data-testid="voucher-applied" className="text-sm text-green-400">
          {VOUCHER_APPLIED}
        </p>
      )}
    </div>
  );
}
